import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { hasAlphaRoleAccess } from "../../hooks/useAlphaAccess";
import { useAuth } from "../../contexts/AuthContext";

export default function SetupV2AccessGuard({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const current = useRef(auth);
  current.current = auth;
  const mounted = useRef(false);
  const [error, setError] = useState(false);
  const [returning, setReturning] = useState(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  if (auth.isLoading) return <p role="status">Loading your account</p>;
  if (!auth.isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasAlphaRoleAccess(auth.user?.roles ?? [])) return <Navigate to={auth.getDefaultLandingPage()} replace />;
  if (auth.isAccountContextLoading || auth.isAccountSwitching) return <p role="status">Verifying your account</p>;
  if (!auth.actorUserId || auth.user?.id !== auth.actorUserId || auth.effectiveUserId !== auth.actorUserId) {
    const returnToSelf = async () => {
      const owner = auth.actorUserId;
      if (!owner || returning) return;
      const stillCurrent = () => mounted.current && current.current.actorUserId === owner;
      setReturning(true);
      setError(false);
      try { await auth.activateAccount(owner); }
      catch { if (stillCurrent()) setError(true); }
      finally { if (stillCurrent()) setReturning(false); }
    };
    return <div className="grid min-h-dvh place-content-center gap-4 bg-v7-canvas p-6 font-v7-sans text-v7-ink">
      <h1 className="text-xl font-bold">Use your own account for setup</h1>
      <p>Setup saves answers to your own account.</p>
      <button type="button" disabled={!auth.actorUserId || returning} onClick={() => void returnToSelf()} className="min-h-11 rounded-lg bg-v7-ink px-5 text-v7-white disabled:opacity-50">{returning ? "Returning..." : "Return to my account"}</button>
      {error && <p role="alert">Could not switch accounts. Please try again.</p>}
    </div>;
  }
  return <React.Fragment key={auth.actorUserId}>{children}</React.Fragment>;
}
